// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS201: Simplify complex destructure assignments
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {range} from '@benchling/js-utils';
import * as d3 from 'd3';

import asm from 'assembly/Asm';
import * as BioJS from 'BioJS';
import {Sequence} from 'biojs/Sequence';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import Display from 'molbio/maps/circular/Display';
import {HomologyAwareFragmentResultData} from 'molbio/maps/circular/HomologyUtil';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import {Model as Selectable} from 'molbio/maps/selectables/Selectables';
import {getAngles} from 'util/geometry/GeometryUtil';

interface SquaresDatum extends d3.DefaultArcObject {
  datum: number;
}

const DEFAULT_BASE_COLOR = '#A57CA6';

// Base hex code to use for each base
const colors: {[base: string]: string} = {
  A: '#C8411E', // red
  C: '#7CB2AB', // blue
  G: '#9BAA53', // green
  T: '#DEBA43', // yellow
  R: DEFAULT_BASE_COLOR,
  Y: DEFAULT_BASE_COLOR,
  M: DEFAULT_BASE_COLOR,
  K: DEFAULT_BASE_COLOR,
  W: DEFAULT_BASE_COLOR,
  S: DEFAULT_BASE_COLOR,
  B: DEFAULT_BASE_COLOR,
  D: DEFAULT_BASE_COLOR,
  H: DEFAULT_BASE_COLOR,
  V: DEFAULT_BASE_COLOR,
  N: '#808080',
};

// Color to use when the base is too small for the text to be seen
const colorsBlock = Object.keys(colors).reduce<{[base: string]: string}>((acc, base) => {
  const color = d3.rgb(colors[base]).brighter(0.3).formatHex();
  acc[base] = color;
  acc[base.toLowerCase()] = color;
  return acc;
}, {});

// Color to use when the base is big enough for the text to be seen
const colorsLetter = Object.keys(colors).reduce<{[base: string]: string}>((acc, base) => {
  acc[base] = colors[base];
  acc[base.toLowerCase()] = colors[base];
  return acc;
}, {});

function computeVisibleBP(leftBP: number, rightBP: number, length: number): Array<number> {
  if (leftBP >= rightBP) {
    return range(leftBP, length - 1).concat(range(0, rightBP - 1));
  } else {
    return range(leftBP, rightBP - 1);
  }
}

function color(d: string, isLetter: boolean): string {
  if (Object.keys(colorsLetter).includes(d)) {
    if (isLetter) {
      return colorsLetter[d];
    } else {
      return colorsBlock[d];
    }
  } else {
    return 'black';
  }
}

export default class CircularDNAView extends RingView<RingLayout> {
  _sequence!: Sequence;

  static initClass(): void {
    this.prototype.className = 'dna';
  }

  override initialize(options: RingComponentOptions<RingLayout>): void {
    super.initialize(options);
    this._sequence = this.model.get('sequence').get('_sequence');
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this._sequence, 'change:finalized change:fragmentResults', () => {
      this.update();
    });
  }

  override updateRadius(): void {
    this.radius.range([this.getOuterRadius(), this.getOuterRadius() - this.layout.get('ringHeight')]);
  }

  override computeOuterRadius(innerRadius: number): number {
    const length = this.model.get('sequence').length();
    const basepairWidth = ((2 * Math.PI) / length) * innerRadius;
    if (basepairWidth > 10) {
      return innerRadius + this.layout.get('ringHeight') * 2;
    } else if (basepairWidth > 3) {
      return innerRadius + Number(this.layout.get('ringHeight'));
    } else {
      return innerRadius + Number(this.layout.get('ringHeight'));
    }
  }

  protected getPiecesData(): Array<HomologyAwareFragmentResultData> {
    const fragmentResultPieces = asm.of(this._sequence).getFragmentResultPieces(this._sequence);
    return fragmentResultPieces.map(([color, start, end]) => {
      const {startAngle, endAngle} = getAngles(start, end, this._sequence.length(), (basePair) =>
        this.basePairToAngle(basePair)
      );
      return {
        pieceStartAngle: startAngle,
        pieceEndAngle: endAngle,
        color,
      };
    });
  }

  override redraw(): void {
    let leftBP: number;
    let rightBP: number;
    const length = this.model.get('sequence').length();
    const root = d3.select(this.el);

    const basepairWidth = ((2 * Math.PI) / length) * this.radius(2);
    const lettersContainer = root.select('.letters');
    const squaresContainer = root.select('.squares');
    const circlesContainer = root.select('.circles');
    const piecesContainer = root.select('.pieces');

    if (this.model.get('fullCircleView') || length < 40) {
      // to avoid left and right from overlapping
      leftBP = 0;
      rightBP = length;
    } else {
      leftBP = (((this.model.get('leftBP') - 10) % length) + length) % length;
      rightBP = (this.model.get('rightBP') + 10) % length;
    }

    // Update data and inner radius
    let letters: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown> = lettersContainer
      .selectAll('.letter')
      .data([]);
    let squares: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown> = squaresContainer
      .selectAll('.square')
      .data([]);
    let circle: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown> = circlesContainer
      .selectAll('.circle')
      .data([]);
    let pieces: d3.Selection<d3.BaseType, HomologyAwareFragmentResultData, d3.BaseType, unknown> =
      piecesContainer.selectAll('.piece').data([]);

    if (!this._sequence.finalized()) {
      const piecesData = this.getPiecesData();
      pieces = piecesContainer.selectAll('.piece').data(piecesData);
      if (basepairWidth > 10) {
        this.layout.set('innerRadius', this.radius(2));
        letters = lettersContainer.selectAll('.letter').data(computeVisibleBP(leftBP, rightBP, length));
      } else {
        this.layout.set('innerRadius', this.radius(1));
      }
    } else if (basepairWidth > 10) {
      this.layout.set('innerRadius', this.radius(2));
      letters = lettersContainer.selectAll('.letter').data(computeVisibleBP(leftBP, rightBP, length));
    } else if (basepairWidth > 3) {
      this.layout.set('innerRadius', this.radius(1));
      squares = squaresContainer.selectAll('.square').data(
        computeVisibleBP(leftBP, rightBP, length).map(
          (d) =>
            ({
              datum: d,
              innerRadius: this.radius(1),
              outerRadius: this.radius(0),
              startAngle: this.angle(d),
              endAngle: this.angle(d + 1),
            }) satisfies SquaresDatum
        )
      );
    } else {
      this.layout.set('innerRadius', this.radius(0) - 4);
      circle = circlesContainer.selectAll('.circle').data(['']);
    }

    // Showing LETTERS
    const enter = letters.join('text').classed('letter', true);
    enter.append('svg:tspan').classed('norm', true);
    enter.append('selectvg:tspan').classed('comp', true);
    letters = letters.merge(enter);

    const ref = this;
    const seq = this.model.get('sequence').get('_sequence');
    const type = this.layout.get('type');
    letters.attr('text-anchor', 'middle').each(function (d: number) {
      const a = ref.basePairToAngle(d + 0.5);
      const r = ref.radius(0) - type.height();
      const deg = Display.angleToDisplayDegrees(a);
      const letter = d3.select(this);

      letter.attr('transform', `rotate(${deg})`).attr('font-weight', 900);

      letter
        .select('.norm')
        .text((d: number) => seq.at(d))
        .attr('fill', (d: number) => color(seq.at(d), true))
        .attr('x', 0)
        .attr('y', -r)
        .attr('dx', 0)
        .attr('dy', 0);

      letter
        .select('.comp')
        .text((d: number) => BioJS.util.complement(seq.at(d), seq.baseTable()))
        .attr('fill', (d: number) => color(BioJS.util.complement(seq.at(d), seq.baseTable()), true))
        .attr('x', 0)
        .attr('y', -r)
        .attr('dx', 0)
        .attr('dy', type.height() + 2);
    });

    // SHOWING SQUARES
    squares
      .join('path')
      .classed('square', true)
      .attr('fill', (d: SquaresDatum) => color(seq.at(d.datum), false))
      .attr('d', d3.arc());

    // SHOWING CIRCLE
    circle
      .join('svg:circle')
      .classed('circle', true)
      .attr('stroke-width', 4)
      .attr('stroke', 'grey')
      .attr('fill', 'none')
      .attr('r', Math.max(this.radius(0) - 2, 0));

    // SHOWING PIECES
    pieces
      .join('svg:path')
      .classed('piece', true)
      .attr('d', ({pieceStartAngle, pieceEndAngle}) =>
        d3.arc()({
          innerRadius: this.getInnerRadius(),
          outerRadius: this.getOuterRadius(),
          startAngle: pieceStartAngle,
          endAngle: pieceEndAngle,
        })
      )
      .attr('fill', ({color}) => color)
      .on('dblclick', (e, {selectRegionOnDoubleClick}) => {
        if (selectRegionOnDoubleClick != null) {
          const {start, end} = selectRegionOnDoubleClick;
          ref.model.get('sequence').get('selection').set({start, end});
          e.stopPropagation();
        }
      });
  }

  override render(): this {
    const root = d3.select(this.el);

    root.append('g').classed('pieces', true);
    root.append('g').classed('letters', true);
    root.append('g').classed('squares', true);
    root.append('g').classed('circles', true);

    return super.render();
  }

  clickAction(e: MouseEvent, d: Selectable): void {}

  dblClickAction(e: MouseEvent, d: Selectable): void {}
}
CircularDNAView.initClass(); // FIXME
